import { z } from 'zod'

/**
 * Types et schemas Zod metier INSi (phase 1, lot 1.C).
 *
 * Ce module porte le contrat metier des trois operations INSi consommees par le
 * socle generique `useAmeliOperation` (lib/Ameli/Core) :
 *  - WS_INS1 : obtenir l'INS avec carte Vitale        (ICIR rechercherInsAvecCarteVitale)
 *  - WS_INS2 : obtenir l'INS sans carte (par traits)  (ICIR rechercherInsAvecTraitsIdentite)
 *  - WS_INS3 : verifier un INS deja connu (unitaire)  (IDIR verifierInsAvecTraitsIdentite)
 *
 * Sources : 00-Annexes/Feat_Ameli/Feature/INSi/{01,03,04}. Les structures de
 * reponse suivent le XSD `INSi_RESULTAT_INS.xsd` (CompteRendu + INDIVIDU) et le
 * XSD IDIR (CrUnitaire) ; l'API (`InsiResponseParser`) les normalise en JSON.
 *
 * ATTENTION : les noms exacts des champs JSON renvoyes par l'API (execute) ne sont
 * pas figes dans les documents de reference. Les schemas ci-dessous refletent la
 * semantique du XSD ; ils doivent etre alignes avec le DTO reel du lot API phase 1.
 */

// ---------------------------------------------------------------------------
// Segments de chemin API : `:operation` sous le service `insi`
// ---------------------------------------------------------------------------

/**
 * Operations INSi cote API (segment `:operation` de
 * /ameli/insi/{operation}/prepare|execute).
 *
 * CONTRAT CANONIQUE : le segment `{operation}` est le NOM SOAP exact de l'operation
 * (cf. enum PHP App\Enum\Ameli\INSi\InsiOperation, source de verite). Le front s'aligne
 * dessus ; ne jamais reintroduire de slug.
 */
export const InsiOperation = {
  /** WS_INS1 : recherche de l'INS a partir de la carte Vitale. */
  RECHERCHE_VITALE: 'rechercherInsAvecCarteVitale',
  /** WS_INS2 : recherche de l'INS a partir des traits d'identite. */
  RECHERCHE_TRAITS: 'rechercherInsAvecTraitsIdentite',
  /** WS_INS3 : verification unitaire d'un INS deja connu. */
  VERIFIER: 'verifierInsAvecTraitsIdentite',
} as const

export type InsiOperation = (typeof InsiOperation)[keyof typeof InsiOperation]

/** Segment `:service` du chemin API pour toutes les operations INSi. */
export const INSI_SERVICE = 'insi'

// ---------------------------------------------------------------------------
// Codes compte-rendu (CodeCR) — INSi/04
// ---------------------------------------------------------------------------

/**
 * Codes de compte-rendu metier observes dans le corpus (INSi/04 § 3.1/3.2) :
 *  - '00' : OK (identite trouvee / INS verifie), bloc INDIVIDU present ;
 *  - '01' : KO (aucune identite trouvee / INS non verifie), pas de bloc INDIVIDU.
 * Le XSD type CodeCR en xsd:string libre : d'autres codes sont possibles, on ne
 * fige donc pas un enum ferme.
 */
export const InsiCodeCR = {
  /** Recherche : une INS unique trouvee. Verification : verifiee avec succes. */
  OK: '00',
  /** Recherche : aucune identite trouvee (« le patient est inconnu »). */
  KO: '01',
  /**
   * Recherche : PLUSIEURS identites trouvees. Le LPS doit l'afficher (test 2.06 cas 1) et,
   * si le lieu de naissance est connu, relancer avec pour lever l'ambiguite (cas 2).
   */
  MULTIPLE: '02',
} as const

// ---------------------------------------------------------------------------
// Traits d'identite (entree WS_INS2 / WS_INS3, sortie TIQ) — INSi/01, INSi/03
// ---------------------------------------------------------------------------

/** Sexe au sens INSi : `M` / `F` (attribut `[A-Z]{1}` du XSD, valeurs observees). */
export const InsiSexeSchema = z.enum(['M', 'F'])
export type InsiSexe = z.infer<typeof InsiSexeSchema>

/**
 * Traits d'identite envoyes en entree (WS_INS2 sans carte, WS_INS3 verification).
 *
 * Regles metier (INSi/01, RG_INS160/180) :
 *  - traits en MAJUSCULES (sinon fault insi_102) : la normalisation est faite a
 *    la construction (voir insiTraits.ts) ;
 *  - `nomNaissance` = nom patronymique seul (jamais le nom d'usage) ;
 *  - un seul `prenom` exploite par appel cote WS_INS2 ;
 *  - `lieuNaissance` = code officiel geographique (COG) du lieu de naissance.
 */
export const InsiTraitsSchema = z.object({
  nomNaissance: z.string().min(1),
  prenom: z.string().min(1),
  sexe: InsiSexeSchema,
  /** Date de naissance ISO `YYYY-MM-DD` (dates incompletes gerees par l'API). */
  dateNaissance: z.string().min(1),
  /** COG du lieu de naissance (optionnel : non exige pour la recherche). */
  lieuNaissance: z.string().optional(),
})
export type InsiTraits = z.infer<typeof InsiTraitsSchema>

// ---------------------------------------------------------------------------
// Beneficiaire carte Vitale (entree WS_INS1) — INSi/03
// ---------------------------------------------------------------------------

/**
 * Beneficiaire lu en carte Vitale, retourne par le companion (vitale/read).
 * Les NOMS DE CHAMPS refletent EXACTEMENT la sortie du companion (CardManager/VitaleParser) :
 * nomPatronymique, nomUsuel, prenomUsuel, dateNaissance (JJ/MM/AAAA), nir, rangDeNaissance...
 * Le mapping vers l'entree WS_INS1 (NomNaissance = nomPatronymique, MatriculeOD = nir,
 * DateNaissance normalisee, RangNaissance) se fait dans useVerifierIdentite.rechercheVitale.
 * `.passthrough()` tolere les champs additionnels du companion.
 *
 * Regle metier (RG_INS160/180, INSi/01) : NomNaissance = nomPatronymique SEUL, jamais le nom d'usage.
 */
export const InsiVitaleBeneficiaireSchema = z
  .object({
    nomPatronymique: z.string().optional(),
    nomUsuel: z.string().optional(),
    prenomUsuel: z.string().optional(),
    /** Date de naissance au format carte JJ/MM/AAAA (l'API la normalise en SSAA-MM-JJ). */
    dateNaissance: z.string().optional(),
    /** NIR certifie / matricule (MatriculeOD/NumIdentifiant de WS_INS1). */
    nir: z.string().optional(),
    nirCertifie: z.string().optional(),
    /** Rang de naissance (RangNaissance de WS_INS1). */
    rangDeNaissance: z.string().optional(),
    /**
     * Qualite du beneficiaire codee carte Vitale : '0' = ouvrant droit (assure principal),
     * autres valeurs = ayants droit. Sert a identifier l'ouvrant droit (MatriculeOD).
     */
    qualBenef: z.string().optional(),
    /** Libelle de qualite ('Assuré', 'Conjoint', 'Enfant', ...), fallback si qualBenef absent. */
    qualite: z.string().optional(),
  })
  .passthrough()
export type InsiVitaleBeneficiaire = z.infer<typeof InsiVitaleBeneficiaireSchema>

// ---------------------------------------------------------------------------
// Contexte PS (`ps`) + carte Vitale (`vitale`) transmis au /prepare
// ---------------------------------------------------------------------------

/**
 * Contexte du professionnel de sante transmis au /prepare (cle `ps` du body).
 * Miroir de InsiPraticienContext::fromArray() cote API. Construit cote front a
 * partir de la lecture CPS du companion (voir insiPraticienContext.ts).
 */
export const InsiPraticienContextSchema = z.object({
  /** NameID de l'assertion PS (RPPS / n° PS). */
  numeroPs: z.string().min(1),
  /** DN complet du certificat de signature CPS (= subject du certificat). */
  subjectDn: z.string().min(1),
  /** Identifiant de facturation (FINESS/SIRET/AM), obligatoire. */
  identifiantFacturation: z.string().min(1),
  /** Code secteur d'activite [CI], obligatoire. */
  secteurActivite: z.string().min(1),
  /** Code specialite (medecins uniquement), facultatif. */
  codeSpecialite: z.string().optional(),
  /** Type de carte (defaut 'CPS'). */
  nameQualifier: z.string().default('CPS'),
})
export type InsiPraticienContext = z.infer<typeof InsiPraticienContextSchema>

/**
 * Contexte carte Vitale (cle `vitale` du body, WS_INS1 uniquement).
 * Miroir de InsiVitaleContext::fromArray() cote API.
 */
export const InsiVitaleContextSchema = z.object({
  /** identifiantFacturation du PS (issuer de l'assertion Vitale). */
  issuer: z.string().min(1),
  /** N° de serie logique de la carte Vitale (12 car.), lu par le companion. */
  numeroSerieLogique: z.string().min(1),
})
export type InsiVitaleContext = z.infer<typeof InsiVitaleContextSchema>

// ---------------------------------------------------------------------------
// Requetes metier (`request`) par operation — miroir des DTO Insi*Request
// ---------------------------------------------------------------------------

/** `request` WS_INS1 (InsiRechercheVitaleRequest::fromArray). */
export const InsiVitaleRequestSchema = z.object({
  nomNaissance: z.string().min(1),
  matriculeOD: z.string().min(1),
  dateNaissance: z.string().min(1),
  rangNaissance: z.string().min(1),
  matriculeIndividu: z.string().optional(),
})
export type InsiVitaleRequest = z.infer<typeof InsiVitaleRequestSchema>

/** `request` WS_INS2 (InsiRechercheTraitsRequest::fromArray). */
export const InsiTraitsRequestSchema = z.object({
  nomNaissance: z.string().min(1),
  prenom: z.string().min(1),
  sexe: InsiSexeSchema,
  dateNaissance: z.string().min(1),
  lieuNaissance: z.string().optional(),
})
export type InsiTraitsRequest = z.infer<typeof InsiTraitsRequestSchema>

/** `request` WS_INS3 (InsiVerificationRequest::fromArray). */
export const InsiVerificationRequestSchema = z.object({
  matricule: z.string().min(1),
  cle: z.string().min(1),
  oid: z.string().min(1),
  nomNaissance: z.string().min(1),
  prenoms: z.array(z.string().min(1)).min(1),
  sexe: InsiSexeSchema,
  dateNaissance: z.string().min(1),
  lieuNaissance: z.string().optional(),
})
export type InsiVerificationRequest = z.infer<typeof InsiVerificationRequestSchema>

// ---------------------------------------------------------------------------
// Reponse execute (WS_INS1 / WS_INS2 : recherche) — XSD INSi_RESULTAT_INS
// ---------------------------------------------------------------------------

/**
 * Identifiant National de Sante (INSACTIF / INSHISTO) : matricule + cle + OID de
 * l'autorite d'affectation (NIR `1.2.250.1.213.1.4.8` / NIA `...4.9`).
 *
 * Miroir de IdentifiantNationalSante::toArray() cote API. `.passthrough()` preserve
 * l'integrite du payload signe HMAC (le PATCH /ins renvoie ce payload tel quel).
 */
export const IdentifiantNationalSanteSchema = z
  .object({
    /** Matricule INS (NIR ou NIA), pattern `\d{6}[A-Za-z0-9]{1}\d{6}`. */
    matricule: z.string(),
    /** Cle de controle a 2 chiffres (nullable dans le DTO API). */
    cle: z.string().nullable().optional(),
    /** OID de l'autorite d'affectation (NIR vs NIA). */
    oid: z.string(),
    /** Type de matricule NIR/NIA deduit de l'OID (null si OID inconnu). */
    type: z.string().nullable().optional(),
    /** Debut de validite (nullable). */
    dateDebut: z.string().nullable().optional(),
    /** Fin de validite (nullable). */
    dateFin: z.string().nullable().optional(),
    /** Vrai si matricule historise (INSHISTO). */
    historise: z.boolean().optional(),
  })
  .passthrough()
export type IdentifiantNationalSante = z.infer<typeof IdentifiantNationalSanteSchema>

/**
 * Traits d'identite de reference (TIQ) retournes par le service.
 * Miroir de InsiTraitsIdentite::toArray() : `prenoms` est une LISTE (jamais `prenom`).
 */
export const InsiTraitsReferenceSchema = z
  .object({
    nomNaissance: z.string().nullable().optional(),
    prenoms: z.array(z.string()).optional(),
    sexe: z.string().nullable().optional(),
    dateNaissance: z.string().nullable().optional(),
    lieuNaissance: z.string().nullable().optional(),
  })
  .passthrough()
export type InsiTraitsReference = z.infer<typeof InsiTraitsReferenceSchema>

/**
 * Compte-rendu metier (CR). Miroir de CompteRendu::toArray() cote API :
 * `code` (CodeCR normalise, nullable), `rawCode` (code brut, fait foi), `libelle`.
 * NB : PAS de `codeCR`/`libelleCR` — le contrat API utilise `code`/`rawCode`/`libelle`.
 */
export const CompteRenduSchema = z
  .object({
    /** Code normalise ('00' OK / '01' KO), null si code non normalise. */
    code: z.string().nullable().optional(),
    /** Code brut renvoye par le service (fait foi, toujours present). */
    rawCode: z.string(),
    /** Libelle informatif (ne jamais interpreter). */
    libelle: z.string().nullable().optional(),
  })
  .passthrough()
export type CompteRendu = z.infer<typeof CompteRenduSchema>

/**
 * Reponse metier d'une recherche (WS_INS1 / WS_INS2), forme A PLAT de
 * InsiResult::toArray() : compteRendu + insActif + insHistorises + traitsReference
 * au top-level (l'API ne renvoie PAS de bloc `individu`). `insActif` n'est present
 * que sur CR '00' : toujours tester `compteRendu.code` avant de le lire.
 *
 * `.passthrough()` : le payload est renvoye tel quel au PATCH /ins et re-verifie par
 * HMAC cote API ; aucune cle ne doit etre stripee par la validation.
 */
export const InsiRechercheResultSchema = z
  .object({
    compteRendu: CompteRenduSchema,
    /** INS actif (INSACTIF), absent si aucune identite trouvee. */
    insActif: IdentifiantNationalSanteSchema.nullable().optional(),
    /** INS historises (INSHISTO, 0..n) — note le `s` final, comme l'API. */
    insHistorises: z.array(IdentifiantNationalSanteSchema).optional(),
    /** Traits d'identite de reference (TIQ). */
    traitsReference: InsiTraitsReferenceSchema.nullable().optional(),
  })
  .passthrough()
export type InsiRechercheResult = z.infer<typeof InsiRechercheResultSchema>

// ---------------------------------------------------------------------------
// Reponse execute (WS_INS3 : verification unitaire) — XSD IDIR CrUnitaire
// ---------------------------------------------------------------------------

/**
 * Reponse de verification unitaire (WS_INS3). Meme forme a plat que la recherche
 * (InsiResult::toArray) : seul `compteRendu` est exploite (code '00' = verifie,
 * '01' = non verifie). `.passthrough()` : integrite du payload signe.
 */
export const InsiVerificationResultSchema = z
  .object({
    compteRendu: CompteRenduSchema,
  })
  .passthrough()
export type InsiVerificationResult = z.infer<typeof InsiVerificationResultSchema>

// ---------------------------------------------------------------------------
// Enveloppe execute — { result, signature, isFault } (contrat canonique)
// ---------------------------------------------------------------------------

/**
 * Enveloppe renvoyee par POST /ameli/insi/{operation}/execute :
 *  - `result`   : payload metier (InsiResult::toArray), succes OU fault ;
 *  - `signature`: HMAC du payload result (a repropager tel quel au PATCH /ins) ;
 *  - `channel`  : canal signe cote serveur (vitale|traits|verification), a repropager tel quel ;
 *  - `expiresAt`: timestamp Unix d'expiration de la signature (TTL), a repropager tel quel ;
 *  - `isFault`  : true si `result` porte une SOAP Fault.
 *
 * `channel` et `expiresAt` entrent dans le calcul du HMAC : les modifier (ou les omettre) fait
 * echouer la verification au PATCH /ins.
 */
export interface InsiExecuteEnvelope<TResult> {
  result: TResult
  signature: string
  channel: InsVerifiedVia
  expiresAt: number
  isFault: boolean
}

/** Fault metier portee dans `result.fault` (InsiResult::toArray forme fault). */
export const InsiFaultSchema = z.object({
  value: z.string().nullable().optional(),
  subcode: z.string().nullable().optional(),
  reason: z.string().nullable().optional(),
  detailCode: z.string().nullable().optional(),
  severite: z.string().nullable().optional(),
  messageId: z.string().nullable().optional(),
})
export type InsiFault = z.infer<typeof InsiFaultSchema>

// ---------------------------------------------------------------------------
// Via de verification / persistance INS — plan phase 1 (insVerifiedVia)
// ---------------------------------------------------------------------------

/** Provenance de l'INS persiste (colonne `insVerifiedVia`). */
export const InsVerifiedVia = {
  VITALE: 'vitale',
  TRAITS: 'traits',
  VERIFICATION: 'verification',
} as const

export type InsVerifiedVia = (typeof InsVerifiedVia)[keyof typeof InsVerifiedVia]
